
/**
 * Processing Purpose Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import useProcessingPurposeStore from '@/store/processing-purpose.store';
import { useTenantStore } from '@/store/tenant.store';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';

export class ProcessingPurposeIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch purposes and update store
     */
    async fetchPurposes(page = 1, pageSize = 10, search = '', sortBy = 'createdAt', sortOrder = 'desc', columnFilters = {}) {
        const store = useProcessingPurposeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setPurposes(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || page,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || 0,
                    itemsPerPage: pagination.pageSize || pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create purpose
     */
    async createPurpose(data) {
        const store = useProcessingPurposeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            // Ensure name is present as it's required by BigdesDefinitionSchema
            const payload = {
                ...data,
                name: data.name || data.description, // Fallback if name is not provided
                tenantId
            };

            const response = await this.service.create(payload);

            toast.success(i18next.t('kvkkProcessingPurpose:form.messages.createSuccess', {
                defaultValue: 'Processing purpose created',
            }));

            if (store.closeForm) store.closeForm();
            await this.fetchPurposes(); // Refresh list

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update purpose
     */
    async updatePurpose(id, data) {
        const store = useProcessingPurposeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const payload = {
                ...data,
                name: data.name || data.description // Fallback
            };

            const response = await this.service.update(id, payload);

            toast.success(i18next.t('kvkkProcessingPurpose:form.messages.updateSuccess', {
                defaultValue: 'Processing purpose updated',
            }));

            if (store.closeForm) store.closeForm();
            await this.fetchPurposes(); // Refresh list

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete purpose
     */
    async deletePurpose(id) {
        const store = useProcessingPurposeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('kvkkProcessingPurpose:form.messages.deleteSuccess', {
                defaultValue: 'Processing purpose deleted',
            }));

            if (store.closeDeleteDialog) store.closeDeleteDialog();
            await this.fetchPurposes(); // Refresh list

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete purposes
     */
    async bulkDeletePurposes(ids) {
        const store = useProcessingPurposeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.bulkDelete(ids);

            toast.success(i18next.t('kvkkProcessingPurpose:form.messages.bulkDeleteSuccess', {
                count: ids.length,
                defaultValue: `${ids.length} processing purposes deleted`,
            }));
            await this.fetchPurposes(); // Refresh list

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Fetch dropdown data
     */
    async fetchDropdownData() {
        try {
            return await fetchDropdownOptionsFromReference('processingPurpose', async () => {
                const response = await this.service.getDropdownParams();
                return { success: true, data: response.data || [] };
            });
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /**
     * Map error to user-friendly message
     */
    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
